/* The stage: the Workshop material direction from design/motion/vectress-motion-study.html, ported to a live board
   and driven by the engine's event log. Machined slabs with enamel pole faces, a dark board that the poles light from
   below, a tilted camera, steel bond bars, and every hit with anticipation, impact and recovery. */
import { BLACK, MAX_PIECES, WHITE, colOf, poleToward, rowOf } from '../../../packages/engine/src/index.ts';
import type { Event, Position, ShapeId, Side } from '../../../packages/engine/src/index.ts';
import { GEOMETRY } from './pieces.ts';
import type { Preview } from './preview.ts';

type RGB = readonly [number, number, number];
type Vec2 = [number, number];
type Vec3 = [number, number, number];

export type View = 'tilt' | 'top';

const COL = {
  pos: [255, 45, 58] as RGB, neg: [47, 123, 255] as RGB, posHot: [255, 150, 160] as RGB, negHot: [150, 190, 255] as RGB,
  violet: [185, 140, 255] as RGB, dust: [200, 210, 230] as RGB, amber: [255, 179, 0] as RGB, ink: [231, 233, 240] as RGB,
};
const N = 9;
const H = 0.22;          // slab height in cells
const REST = 0.12;       // resting glow of a pole face
const LIFT = 0.34;       // how high a piece is picked up before it moves

/* ---------------- maths ---------------- */
const clamp = (v: number, a: number, b: number): number => (v < a ? a : v > b ? b : v);
const seg = (t: number, a: number, b: number): number => clamp((t - a) / (b - a), 0, 1);
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
const E = {
  outCubic: (t: number): number => 1 - Math.pow(1 - t, 3),
  inCubic: (t: number): number => t * t * t,
  inOutCubic: (t: number): number => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2),
  inQuad: (t: number): number => t * t,
  outQuad: (t: number): number => 1 - (1 - t) * (1 - t),
  outBack: (t: number): number => { const c = 1.35; return 1 + (c + 1) * Math.pow(t - 1, 3) + c * Math.pow(t - 1, 2); },
  smooth: (t: number): number => t * t * (3 - 2 * t),
  linear: (t: number): number => t,
};
const rgba = (c: RGB, a: number): string => `rgba(${c[0] | 0},${c[1] | 0},${c[2] | 0},${a})`;
const mix = (a: RGB, b: RGB, t: number): RGB => [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)];
const scaleC = (c: RGB, k: number): RGB => [c[0] * k, c[1] * k, c[2] * k];
const LIGHT: Vec3 = (() => { const v: Vec3 = [-0.45, -0.55, 0.7]; const L = Math.hypot(...v); return [v[0] / L, v[1] / L, v[2] / L]; })();

/* ---------------- scene types ---------------- */
type Prop = 'x' | 'y' | 'z' | 'sz' | 'alpha' | 'gPos' | 'gNeg' | 'fall';
interface Model extends Record<Prop, number> {
  id: number; shape: ShapeId; owner: Side; alive: boolean;
  /** direction the piece is falling in, once it has left the board */
  fallDir: Vec2 | null;
  trail: Vec2[];
}
interface Bar { a: number; b: number; born: number; dying: number }
interface Tween { id: number; prop: Prop; t0: number; t1: number; from: number; to: number; ease: (t: number) => number }
interface Cue { t: number; fn: () => void; fired: boolean }
interface Field { a: Vec3; b: Vec3; kind: 'repel' | 'attract'; col: RGB; t0: number; t1: number }
interface Particle { x: number; y: number; z: number; vx: number; vy: number; vz: number; life: number; age: number; col: RGB; r: number }
interface Ring { x: number; y: number; age: number; life: number; col: RGB; size: number }
interface Proj { x: number; y: number; d: number; s: number }
interface Geom { top: Vec3[]; bot: Vec3[]; center: Vec3; nrm: Vec3[] }

export interface StageOptions { onCell: (cell: number) => void }

export class Stage {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly onCell: (cell: number) => void;
  private readonly models: Model[] = [];
  private bars: Bar[] = [];
  private view: View = 'tilt';
  private flipped = false;
  private speed = 1;
  private reducedMotion = false;
  private iconMode = false;

  private W = 300; private Hpx = 300; private dpr = 1;
  private cam = { tx: 4.5, ty: 4.5, dist: 8.6, pitch: 57 * Math.PI / 180, fov: 100, cx: 150, cy: 150, ox: 0, oy: 0, punch: 1 };

  // story
  private T = 0;
  private tweens: Tween[] = [];
  private cues: Cue[] = [];
  private fields: Field[] = [];
  private slow: Array<[number, number]> = [];
  private storyEnd = -1;
  private resolveStory: (() => void) | null = null;
  private particles: Particle[] = [];
  private rings: Ring[] = [];
  private flash = 0;
  private shake = 0;

  // interaction
  private selected: number | null = null;
  private previews: Preview[] = [];
  private pressed: Vec2 | null = null;
  private dirty = true;
  private last = performance.now();
  private raf = 0;
  private observer: ResizeObserver | null = null;

  constructor(canvas: HTMLCanvasElement, opts: StageOptions) {
    this.canvas = canvas;
    this.onCell = opts.onCell;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no 2d context');
    this.ctx = ctx;
    this.reducedMotion = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    for (let i = 0; i < MAX_PIECES; i++) {
      this.models.push({ id: i, shape: 0, owner: WHITE, alive: false, x: 0, y: 0, z: 0, sz: 1, alpha: 1, gPos: REST, gNeg: REST, fall: 0, fallDir: null, trail: [] });
    }
    canvas.addEventListener('pointerdown', ev => { this.pressed = [ev.clientX, ev.clientY]; });
    canvas.addEventListener('pointerup', ev => {
      if (!this.pressed) return;
      const moved = Math.hypot(ev.clientX - this.pressed[0], ev.clientY - this.pressed[1]);
      this.pressed = null;
      if (moved > 10) return;
      const cell = this.cellAt(ev.clientX, ev.clientY);
      if (cell >= 0) this.onCell(cell);
    });
    if (typeof ResizeObserver === 'function') {
      this.observer = new ResizeObserver(() => this.resize());
      this.observer.observe(canvas);
    }
    this.resize();
    this.raf = requestAnimationFrame(t => { this.last = t; this.frame(t); });
  }

  destroy(): void { cancelAnimationFrame(this.raf); this.observer?.disconnect(); }

  /* ---------------- public state ---------------- */

  setView(v: View): void { this.view = v; this.layout(); this.dirty = true; }
  setFlipped(f: boolean): void { if (this.flipped !== f) { this.flipped = f; this.dirty = true; } }
  setSpeed(k: number): void { this.speed = k; }
  setMarks(selected: number | null, previews: Preview[]): void { this.selected = selected; this.previews = previews; this.dirty = true; }

  /** Show a position at rest. Cancels any story in progress. */
  setPosition(pos: Position): void {
    this.tweens = []; this.cues = []; this.fields = []; this.slow = []; this.storyEnd = -1;
    if (this.resolveStory) { const r = this.resolveStory; this.resolveStory = null; r(); }
    for (let i = 0; i < MAX_PIECES; i++) {
      const m = this.models[i];
      m.alive = pos.alive[i] === 1;
      m.shape = pos.shape[i] as ShapeId; m.owner = pos.owner[i] as Side;
      m.x = pos.col[i] + 0.5; m.y = pos.row[i] + 0.5; m.z = 0; m.sz = 1; m.alpha = 1; m.gPos = REST; m.gNeg = REST; m.fall = 0; m.fallDir = null; m.trail = [];
    }
    this.bars = [];
    for (let i = 0; i < MAX_PIECES; i++) for (let j = i + 1; j < MAX_PIECES; j++) if (pos.alive[i] && (pos.bonds[i] & (1 << j))) this.bars.push({ a: i, b: j, born: -1e9, dying: -1 });
    this.dirty = true;
  }

  /** Icon mode: a single piece, framed tightly, no board. */
  showIcon(shape: ShapeId, side: Side): void {
    this.iconMode = true;
    for (const m of this.models) m.alive = false;
    const m = this.models[0];
    m.alive = true; m.shape = shape; m.owner = side; m.x = 4.5; m.y = 4.5; m.z = 0; m.sz = 1; m.alpha = 1; m.gPos = 0.35; m.gNeg = 0.35;
    this.cam.pitch = 62 * Math.PI / 180;
    this.layout();
    this.dirty = true;
  }

  /** Play a move as choreography. Resolves when the board has settled. The caller should then setPosition(after). */
  play(before: Position, events: Event[]): Promise<void> {
    this.setPosition(before);
    const models = this.models;
    let t = 0;
    // Where each piece will be as the story is built. Kept apart from the models, which must stay at the start
    // position until their tweens begin, or a piece would be drawn at its destination before it has moved.
    const at = new Map<number, Vec2>();
    for (const m of models) at.set(m.id, [m.x, m.y]);
    const P = (id: number): Vec2 => at.get(id)!;
    const tw = (id: number, prop: Prop, t0: number, t1: number, from: number, to: number, ease = E.linear): void => { this.tweens.push({ id, prop, t0, t1, from, to, ease }); };
    const cue = (when: number, fn: () => void): void => { this.cues.push({ t: when, fn, fired: false }); };
    const slowmo = (a: number, b: number): void => { this.slow.push([a, b]); };
    const shove = (id: number, dc: number, dr: number, t0: number): void => {
      const [x0, y0] = P(id);
      tw(id, 'x', t0, t0 + 0.34, x0, x0 + dc, E.outBack);
      tw(id, 'y', t0, t0 + 0.34, y0, y0 + dr, E.outBack);
      at.set(id, [x0 + dc, y0 + dr]);
    };
    const fall = (id: number, dc: number, dr: number, t0: number): void => {
      const m = models[id];
      const L = Math.hypot(dc, dr) || 1;
      m.fallDir = [dc / L, dr / L];
      tw(id, 'fall', t0, t0 + 0.85, 0, 1);
      cue(t0 + 0.12, () => {
        const p = this.edgePivot(m);
        this.flash = Math.max(this.flash, 0.35); this.shake = Math.max(this.shake, 1.0);
        this.spawnRing([p[0], p[1]], m.owner === WHITE ? COL.posHot : COL.negHot, 1.1);
        this.spawnSparks([p[0], p[1], 0.05], COL.posHot, 22, 1.3);
      });
      slowmo(t0, t0 + 0.5);
    };
    const flare = (id: number, pole: number, t0: number): void => {
      const prop: Prop = pole > 0 ? 'gPos' : 'gNeg';
      tw(id, prop, t0, t0 + 0.12, REST, 1, E.smooth);
      tw(id, prop, t0 + 0.45, t0 + 1.1, 1, REST, E.smooth);
    };

    // 1. the move: lift, glide, drop, squash
    const move = events.find(e => e.kind === 'move');
    if (move && move.kind === 'move') {
      const dr = rowOf(move.to) - rowOf(move.from), dc = colOf(move.to) - colOf(move.from);
      const dist = Math.max(Math.abs(dr), Math.abs(dc));
      const tGlide = 0.22, tDrop = tGlide + 0.30 + 0.10 * dist, tLand = tDrop + 0.16;
      for (const id of move.body) {
        const [x0, y0] = P(id);
        tw(id, 'z', 0, tGlide, 0, LIFT, E.outCubic);
        tw(id, 'x', tGlide, tDrop, x0, x0 + dc, E.inOutCubic);
        tw(id, 'y', tGlide, tDrop, y0, y0 + dr, E.inOutCubic);
        tw(id, 'z', tDrop, tLand, LIFT, 0, E.inQuad);
        tw(id, 'sz', tLand, tLand + 0.09, 1, 0.82, E.outQuad);
        tw(id, 'sz', tLand + 0.09, tLand + 0.18, 0.82, 1.06, E.outQuad);
        tw(id, 'sz', tLand + 0.18, tLand + 0.27, 1.06, 1, E.smooth);
        at.set(id, [x0 + dc, y0 + dr]);
      }
      for (const e of events) if (e.kind === 'drag-off') fall(e.piece, dc, dr, tDrop);
      const mover = models[move.piece];
      cue(0.02, () => { for (const id of move.body) models[id].trail = []; });
      cue(tLand, () => {
        this.shake = Math.max(this.shake, 0.45);
        this.spawnRing([mover.x, mover.y], COL.dust, 1);
        this.spawnSparks([mover.x, mover.y, 0.02], COL.dust, 10, 0.5);
      });
      t = tLand + 0.12;
    }
    for (const e of events) if (e.kind === 'unbond') {
      for (const bar of this.bars) if (bar.a === e.piece || bar.b === e.piece) bar.dying = 0.05;
    }

    // 2. the waves
    const waves = new Map<number, Event[]>();
    for (const e of events) if ('wave' in e && e.kind !== 'wave') { let list = waves.get(e.wave); if (!list) { list = []; waves.set(e.wave, list); } list.push(e); }
    for (const wave of [...waves.keys()].sort((a, b) => a - b)) {
      const list = waves.get(wave)!;
      const tStart = t, chargeEnd = tStart + 0.22;
      let pushes = 0, fell = false, bonded = false;
      for (const e of list) {
        if (e.kind !== 'contact') continue;
        const a = models[e.a], b = models[e.b];
        const [ax, ay] = P(e.a), [bx, by] = P(e.b);
        const pa = poleToward(a.shape, a.owner, e.dr, e.dc), pb = poleToward(b.shape, b.owner, -e.dr, -e.dc);
        flare(e.a, pa, tStart); flare(e.b, pb, tStart);
        const dir: Vec2 = [e.dc, e.dr];
        const from: Vec3 = [ax + dir[0] * 0.42, ay + dir[1] * 0.42, H * 0.6];
        const to: Vec3 = [bx - dir[0] * 0.42, by - dir[1] * 0.42, H * 0.6];
        const col = e.effect === 'attract' ? COL.violet : pa > 0 ? COL.pos : COL.neg;
        this.fields.push({ a: from, b: to, kind: e.effect === 'push' ? 'repel' : 'attract', col, t0: tStart, t1: chargeEnd + 0.18 });
        const mid: Vec3 = [(ax + bx) / 2, (ay + by) / 2, H * 0.5];
        cue(chargeEnd - 0.04, () => {
          this.spawnSparks(mid, e.effect === 'attract' ? COL.violet : pa > 0 ? COL.posHot : COL.negHot, e.effect === 'push' ? 14 : 8, e.effect === 'push' ? 1.0 : 0.7);
          if (e.effect === 'push') this.shake = Math.max(this.shake, 0.4);
        });
      }
      for (const e of list) {
        if (e.kind === 'push') {
          const t0 = chargeEnd + pushes * 0.05;
          e.body.forEach((id, k) => {
            if (e.to[k] >= 0) shove(id, e.dc, e.dr, t0);
            else { fall(id, e.dc, e.dr, t0); fell = true; }
          });
          pushes++;
        } else if (e.kind === 'blocked' && (e.dr || e.dc)) {
          for (const id of e.body) {
            const [x0, y0] = P(id);
            tw(id, 'x', chargeEnd, chargeEnd + 0.09, x0, x0 + e.dc * 0.06, E.outQuad);
            tw(id, 'x', chargeEnd + 0.09, chargeEnd + 0.2, x0 + e.dc * 0.06, x0, E.smooth);
            tw(id, 'y', chargeEnd, chargeEnd + 0.09, y0, y0 + e.dr * 0.06, E.outQuad);
            tw(id, 'y', chargeEnd + 0.09, chargeEnd + 0.2, y0 + e.dr * 0.06, y0, E.smooth);
          }
        }
      }
      const tBond = chargeEnd + (pushes ? 0.34 + (pushes - 1) * 0.05 : 0.1);
      for (const e of list) {
        if (e.kind !== 'bond') continue;
        bonded = true;
        const a = models[e.a], b = models[e.b];
        this.bars.push({ a: e.a, b: e.b, born: tBond, dying: -1 });
        cue(tBond, () => {
          this.flash = Math.max(this.flash, 0.4); this.shake = Math.max(this.shake, 0.5);
          const mid: Vec2 = [(a.x + b.x) / 2, (a.y + b.y) / 2];
          this.spawnRing(mid, COL.violet, 0.9);
          this.spawnSparks([mid[0], mid[1], H], COL.violet, 20, 1.2);
        });
        slowmo(tBond - 0.12, tBond + 0.22);
      }
      t = tBond + (bonded ? 0.32 : pushes ? 0.08 : 0.02) + (fell ? 0.45 : 0);
    }

    this.storyEnd = t + 0.22;
    this.T = 0;
    for (const bar of this.bars) if (bar.born > -1e8) bar.born = Math.max(bar.born, 0);
    this.dirty = true;
    return new Promise<void>(resolve => { this.resolveStory = resolve; });
  }

  /* ---------------- camera ---------------- */

  private resize(): void {
    const r = this.canvas.getBoundingClientRect();
    this.dpr = Math.min(2, window.devicePixelRatio || 1);
    this.W = Math.max(2, r.width); this.Hpx = Math.max(2, r.height);
    this.canvas.width = Math.round(this.W * this.dpr); this.canvas.height = Math.round(this.Hpx * this.dpr);
    this.layout();
    this.dirty = true;
  }

  /** Fit the board (or the icon piece) into the canvas for the current view. */
  private layout(): void {
    const c = this.cam;
    if (!this.iconMode) { c.pitch = (this.view === 'top' ? 86 : 57) * Math.PI / 180; c.dist = this.view === 'top' ? 12.5 : 8.6; }
    c.tx = 4.5; c.ty = this.iconMode ? 4.5 : 4.6;
    c.fov = 1; c.cx = 0; c.cy = 0; c.ox = 0; c.oy = 0;
    const pts: Proj[] = [];
    if (this.iconMode) {
      for (const x of [3.95, 5.05]) for (const y of [3.95, 5.05]) for (const z of [0, H + 0.05]) pts.push(this.projectView(x, y, z));
    } else {
      for (const x of [0, N]) for (const y of [0, N]) for (const z of [-0.4, 0]) pts.push(this.projectView(x, y, z));
      pts.push(this.projectView(0, N, 0.5), this.projectView(N, N, 0.5), this.projectView(4.5, -0.5, 0));
    }
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const p of pts) { x0 = Math.min(x0, p.x); y0 = Math.min(y0, p.y); x1 = Math.max(x1, p.x); y1 = Math.max(y1, p.y); }
    const margin = this.iconMode ? 0.02 : 0.045;
    const k = Math.min((this.W * (1 - 2 * margin)) / (x1 - x0), (this.Hpx * (1 - 2 * margin)) / (y1 - y0));
    c.fov = k;
    c.cx = this.W / 2 - k * (x0 + x1) / 2;
    c.cy = this.Hpx / 2 - k * (y0 + y1) / 2;
  }

  /** World to view: the board turns 180 degrees for the other seat. */
  private V(x: number, y: number): Vec2 { return this.flipped ? [N - x, N - y] : [x, y]; }

  private projectView(x: number, y: number, z: number): Proj {
    const c = this.cam;
    const cp = Math.cos(c.pitch), sp = Math.sin(c.pitch);
    const vx = x - c.tx, vy = (y - c.ty) + c.dist * cp, vz = z - c.dist * sp;
    const depth = vy * cp - vz * sp, up = vy * sp + vz * cp;
    const f = c.fov * c.punch / Math.max(0.05, depth);
    return { x: c.cx + c.ox + f * vx, y: c.cy + c.oy - f * up, d: depth, s: f };
  }
  private project(x: number, y: number, z: number): Proj { const [vx, vy] = this.V(x, y); return this.projectView(vx, vy, z); }
  private camPos(): Vec3 { const c = this.cam; const cp = Math.cos(c.pitch), sp = Math.sin(c.pitch); return [c.tx, c.ty - c.dist * cp, c.dist * sp]; }

  /** Board cell under a client point, or -1. */
  cellAt(clientX: number, clientY: number): number {
    const r = this.canvas.getBoundingClientRect();
    const px = clientX - r.left, py = clientY - r.top;
    for (let row = 0; row < N; row++) for (let col = 0; col < N; col++) {
      const q = [this.project(col, row, 0), this.project(col + 1, row, 0), this.project(col + 1, row + 1, 0), this.project(col, row + 1, 0)];
      if (pointInPoly(px, py, q)) return row * N + col;
    }
    return -1;
  }

  /* ---------------- story clock ---------------- */

  private frame(now: number): void {
    const dt = Math.min(0.05, (now - this.last) / 1000);
    this.last = now;
    const running = this.storyEnd >= 0;
    let sp = this.speed * (this.reducedMotion ? 2.5 : 1);
    if (running && !this.reducedMotion) for (const [a, b] of this.slow) if (this.T >= a && this.T < b) sp *= 0.5;
    const sdt = dt * sp;
    if (running) {
      const prev = this.T;
      this.T += sdt;
      for (const c of this.cues) if (!c.fired && prev <= c.t && this.T >= c.t) { c.fired = true; c.fn(); }
      this.applyTweens(this.T);
      for (const m of this.models) {
        if (!m.alive) continue;
        const lastp = m.trail[m.trail.length - 1];
        const moving = !lastp || Math.hypot(m.x - lastp[0], m.y - lastp[1]) > 0.01;
        if (moving && m.fall <= 0) m.trail.push([m.x, m.y]); else if (m.trail.length) m.trail.shift();
        if (m.trail.length > 7) m.trail.shift();
      }
      if (this.T >= this.storyEnd) {
        this.storyEnd = -1;
        for (const c of this.cues) if (!c.fired) { c.fired = true; c.fn(); }
        this.applyTweens(1e9);
        const r = this.resolveStory; this.resolveStory = null; r?.();
      }
      this.dirty = true;
    }
    // effects live in story time even when no story runs
    for (const q of this.particles) { q.age += sdt; q.x += q.vx * sdt; q.y += q.vy * sdt; q.z += q.vz * sdt; q.vz -= 9 * sdt; if (q.z < 0 && q.vz < 0) { q.z = 0; q.vz *= -0.35; q.vx *= 0.6; q.vy *= 0.6; } }
    if (this.particles.length) { this.particles = this.particles.filter(q => q.age < q.life); this.dirty = true; }
    for (const r of this.rings) r.age += sdt;
    if (this.rings.length) { this.rings = this.rings.filter(r => r.age < r.life); this.dirty = true; }
    if (this.flash > 0.004 || this.shake > 0.004) { this.flash *= Math.exp(-dt * 7); this.shake *= Math.exp(-dt * 8); this.dirty = true; }
    else { this.flash = 0; this.shake = 0; }
    const barsBusy = this.bars.some(b => b.dying >= 0 || (b.born > -1e8 && this.T - b.born < 0.7));
    if (barsBusy) this.dirty = true;
    if (this.dirty) { this.dirty = false; this.render(); }
    this.raf = requestAnimationFrame(t => this.frame(t));
  }

  private applyTweens(t: number): void {
    for (const tw of this.tweens) {
      if (t < tw.t0) continue;
      const u = tw.t1 > tw.t0 ? clamp((t - tw.t0) / (tw.t1 - tw.t0), 0, 1) : 1;
      this.models[tw.id][tw.prop] = lerp(tw.from, tw.to, tw.ease(u));
    }
  }

  private spawnSparks(w: Vec3, col: RGB, n: number, power: number): void {
    if (this.reducedMotion) return;
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2, sp = (0.6 + Math.random() * 1.4) * power;
      this.particles.push({ x: w[0], y: w[1], z: w[2], vx: Math.cos(a) * sp, vy: Math.sin(a) * sp * 0.6, vz: 1.5 * power + Math.random() * 2.5 * power, life: 0.45 + Math.random() * 0.35, age: 0, col, r: 2.5 + Math.random() * 3.5 });
    }
  }
  private spawnRing(w: Vec2, col: RGB, size: number): void {
    if (this.reducedMotion) return;
    this.rings.push({ x: w[0], y: w[1], age: 0, life: 0.55, col, size });
  }

  /* ---------------- geometry ---------------- */

  /** Where a falling piece pivots: the point on the board edge in front of it. */
  private edgePivot(m: Model): Vec2 {
    const [dx, dy] = m.fallDir ?? [0, 1];
    const tx = dx > 0 ? (N - m.x) / dx : dx < 0 ? -m.x / dx : Infinity;
    const ty = dy > 0 ? (N - m.y) / dy : dy < 0 ? -m.y / dy : Infinity;
    const t = Math.min(tx, ty);
    return [m.x + dx * t, m.y + dy * t];
  }

  private pieceGeom(m: Model): Geom {
    const G = GEOMETRY[m.shape];
    const sg = m.owner === WHITE ? 1 : -1;             // White faces +row, Black is turned around
    const h = H * m.sz;
    const xy: Vec2[] = G.poly.map(([px, py]) => [m.x + sg * px, m.y - sg * py]);
    let cx = 0, cy = 0; for (const [x, y] of xy) { cx += x / xy.length; cy += y / xy.length; }
    let top: Vec3[] = xy.map(([x, y]) => [x, y, m.z + h]);
    let bot: Vec3[] = xy.map(([x, y]) => [x, y, m.z]);
    let center: Vec3 = [cx, cy, m.z + h / 2];
    let nrm: Vec3[] = G.edges.map(e => { const mx = (xy[e.a][0] + xy[e.b][0]) / 2 - cx, my = (xy[e.a][1] + xy[e.b][1]) / 2 - cy, L = Math.hypot(mx, my) || 1; return [mx / L, my / L, 0]; });
    if (m.fall > 0 && m.fallDir) {
      const u = m.fall;
      const theta = (Math.PI / 2) * E.inQuad(seg(u, 0, 0.45)) + 3.2 * Math.max(0, u - 0.45);
      const tf = Math.max(0, u - 0.42);
      const along = 1.5 * tf + 0.6 * tf * tf, down = -4.5 * tf * tf;
      const P = this.edgePivot(m), d = m.fallDir, perp: Vec2 = [-d[1], d[0]];
      const rot = (q: Vec3, move: boolean): Vec3 => {
        const rx = q[0] - P[0], ry = q[1] - P[1];
        const a = rx * d[0] + ry * d[1], l = rx * perp[0] + ry * perp[1], hh = q[2];
        const a2 = a * Math.cos(theta) + hh * Math.sin(theta), h2 = -a * Math.sin(theta) + hh * Math.cos(theta);
        const aa = a2 + (move ? along : 0), zz = h2 + (move ? down : 0);
        return [P[0] + aa * d[0] + l * perp[0], P[1] + aa * d[1] + l * perp[1], zz];
      };
      top = top.map(q => rot(q, true)); bot = bot.map(q => rot(q, true)); center = rot(center, true);
      nrm = nrm.map(n => { const a = n[0] * d[0] + n[1] * d[1], l = n[0] * perp[0] + n[1] * perp[1]; const a2 = a * Math.cos(theta), h2 = -a * Math.sin(theta); return [a2 * d[0] + l * perp[0], a2 * d[1] + l * perp[1], h2]; });
    }
    return { top, bot, center, nrm };
  }

  /* ---------------- drawing ---------------- */

  private poly(pts: Proj[]): void { const c = this.ctx; c.beginPath(); pts.forEach((q, i) => (i ? c.lineTo(q.x, q.y) : c.moveTo(q.x, q.y))); c.closePath(); }
  private projAll(ws: Vec3[]): Proj[] { return ws.map(w => this.project(w[0], w[1], w[2])); }
  private circleWorld(x: number, y: number, z: number, r: number, n: number): Proj[] {
    const pts: Proj[] = [];
    for (let i = 0; i < n; i++) { const a = i / n * Math.PI * 2; pts.push(this.project(x + r * Math.cos(a), y + r * Math.sin(a), z)); }
    return pts;
  }

  private render(): void {
    const c = this.ctx, W = this.W, Hh = this.Hpx;
    c.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    const sh = this.reducedMotion ? 0 : this.shake;
    this.cam.ox = (Math.random() * 2 - 1) * 9 * sh; this.cam.oy = (Math.random() * 2 - 1) * 9 * sh;
    this.cam.punch = 1 + 0.04 * sh;

    if (this.iconMode) { c.clearRect(0, 0, W, Hh); }
    else {
      const bg = c.createRadialGradient(W / 2, Hh * 0.55, W * 0.1, W / 2, Hh * 0.55, Math.max(W, Hh) * 0.9);
      bg.addColorStop(0, '#0C0E15'); bg.addColorStop(1, '#030406');
      c.fillStyle = bg; c.fillRect(0, 0, W, Hh);
      this.drawBoard();
      this.drawSelection();
    }
    this.drawGlowPools();
    this.drawShadows();
    if (!this.iconMode) { this.drawFields(); this.drawRings(); }

    const alive = this.models.filter(m => m.alive && m.alpha > 0.002);
    const order = alive.map(m => ({ m, d: this.project(m.x, m.y, 0).d + (m.fall > 0 ? -0.001 : 0) })).sort((a, b) => b.d - a.d);
    const drawn = new Set<number>();
    for (const o of order) {
      this.drawPiece(o.m);
      drawn.add(o.m.id);
      for (const bar of this.bars) if (drawn.has(bar.a) && drawn.has(bar.b) && (bar.a === o.m.id || bar.b === o.m.id)) this.drawBar(bar);
    }
    if (!this.iconMode) { this.drawMarks(); this.drawParticles(); this.drawPost(); }
  }

  private drawBoard(): void {
    const c = this.ctx;
    const c00 = this.projectView(0, 0, 0), c90 = this.projectView(N, 0, 0), c99 = this.projectView(N, N, 0), c09 = this.projectView(0, N, 0);
    const b00 = this.projectView(0, 0, -0.4), b90 = this.projectView(N, 0, -0.4);
    this.poly([c00, c90, b90, b00]);
    const gf = c.createLinearGradient(0, c00.y, 0, b00.y); gf.addColorStop(0, '#141821'); gf.addColorStop(1, '#07080C'); c.fillStyle = gf; c.fill();
    this.poly([c00, c90, c99, c09]);
    const gt = c.createLinearGradient(0, c09.y, 0, c00.y); gt.addColorStop(0, '#12151D'); gt.addColorStop(1, '#1E222C'); c.fillStyle = gt; c.fill();
    c.fillStyle = 'rgba(255,255,255,0.028)';
    for (let r = 0; r < N; r++) for (let cc = 0; cc < N; cc++) if ((r + cc) % 2 === 0) { this.poly([this.projectView(cc, r, 0), this.projectView(cc + 1, r, 0), this.projectView(cc + 1, r + 1, 0), this.projectView(cc, r + 1, 0)]); c.fill(); }
    c.strokeStyle = 'rgba(140,152,178,0.30)'; c.lineWidth = Math.max(0.8, this.W / 700);
    for (let i = 0; i <= N; i++) {
      const a = this.projectView(i, 0, 0), b = this.projectView(i, N, 0); c.beginPath(); c.moveTo(a.x, a.y); c.lineTo(b.x, b.y); c.stroke();
      const d = this.projectView(0, i, 0), e = this.projectView(N, i, 0); c.beginPath(); c.moveTo(d.x, d.y); c.lineTo(e.x, e.y); c.stroke();
    }
    this.poly([c00, c90, c99, c09]); c.strokeStyle = 'rgba(170,190,230,0.45)'; c.lineWidth = Math.max(1, this.W / 500); c.stroke();
    c.beginPath(); c.moveTo(c00.x, c00.y); c.lineTo(c90.x, c90.y); c.strokeStyle = 'rgba(200,215,245,0.6)'; c.lineWidth = Math.max(1.4, this.W / 360); c.stroke();
    // notation, in view space so it stays on the near and left edges whichever way the board is turned
    c.fillStyle = '#5F6878'; c.textAlign = 'center'; c.textBaseline = 'middle';
    for (let i = 0; i < N; i++) {
      const col = this.flipped ? N - 1 - i : i, row = this.flipped ? N - 1 - i : i;
      const p = this.projectView(i + 0.5, -0.32, 0);
      c.font = `500 ${Math.max(9, p.s * 0.26)}px "IBM Plex Mono", monospace`; c.fillText(String.fromCharCode(65 + col), p.x, p.y);
      const q = this.projectView(-0.36, i + 0.5, 0);
      c.font = `500 ${Math.max(9, q.s * 0.26)}px "IBM Plex Mono", monospace`; c.fillText(String(row + 1), q.x, q.y);
    }
  }

  private drawSelection(): void {
    if (this.selected === null) return;
    const m = this.models[this.selected];
    if (!m.alive) return;
    const col = Math.floor(m.x), row = Math.floor(m.y);
    this.poly([this.project(col, row, 0.002), this.project(col + 1, row, 0.002), this.project(col + 1, row + 1, 0.002), this.project(col, row + 1, 0.002)]);
    this.ctx.fillStyle = 'rgba(231,233,240,0.10)'; this.ctx.fill();
    this.ctx.strokeStyle = 'rgba(231,233,240,0.55)'; this.ctx.lineWidth = Math.max(1, this.W / 500); this.ctx.stroke();
  }

  private drawGlowPools(): void {
    const c = this.ctx;
    c.globalCompositeOperation = 'lighter';
    for (const m of this.models) {
      if (!m.alive || m.alpha <= 0 || m.fall > 0) continue;
      const g = this.pieceGeom(m), G = GEOMETRY[m.shape];
      G.edges.forEach((e, i) => {
        const inten = e.pole > 0 ? m.gPos : m.gNeg;
        if (inten < 0.03) return;
        const mx = (g.bot[e.a][0] + g.bot[e.b][0]) / 2, my = (g.bot[e.a][1] + g.bot[e.b][1]) / 2;
        const q = this.project(mx + g.nrm[i][0] * 0.12, my + g.nrm[i][1] * 0.12, 0.005);
        const r = q.s * (0.55 + 0.6 * inten), col = e.pole > 0 ? COL.pos : COL.neg;
        const gr = c.createRadialGradient(q.x, q.y, 0, q.x, q.y, r);
        gr.addColorStop(0, rgba(col, (0.30 * inten + 0.06) * m.alpha)); gr.addColorStop(1, rgba(col, 0));
        c.fillStyle = gr; c.beginPath(); c.arc(q.x, q.y, r, 0, Math.PI * 2); c.fill();
      });
    }
    c.globalCompositeOperation = 'source-over';
  }

  private drawShadows(): void {
    const c = this.ctx;
    for (const m of this.models) {
      if (!m.alive || m.alpha <= 0 || m.fall > 0) continue;
      if (m.z > 0.01) {
        const r = 0.42 + 0.5 * m.z, a = 0.55 / (1 + 4 * m.z);
        const pts = this.circleWorld(m.x, m.y, 0.004, r, 28), p = this.project(m.x, m.y, 0);
        const gr = c.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.s * r); gr.addColorStop(0, `rgba(0,0,0,${a})`); gr.addColorStop(1, 'rgba(0,0,0,0)');
        this.poly(pts); c.fillStyle = gr; c.fill();
      } else {
        const g = this.pieceGeom(m);
        const pts = this.projAll(g.bot.map(q => [q[0] * 1.04 - m.x * 0.04, q[1] * 1.04 - m.y * 0.04 - 0.03, 0.003] as Vec3));
        this.poly(pts); c.fillStyle = 'rgba(0,0,0,0.55)'; c.fill();
      }
    }
  }

  private drawFields(): void {
    const c = this.ctx;
    const live = this.fields.filter(f => this.T >= f.t0 && this.T <= f.t1);
    if (!live.length) return;
    c.globalCompositeOperation = 'lighter'; c.lineCap = 'round';
    for (const f of live) {
      const u = seg(this.T, f.t0, f.t1);
      const alpha = E.smooth(seg(u, 0, 0.35)) * (1 - E.smooth(seg(u, 0.55, 1)));
      if (alpha < 0.01) continue;
      const [ax, ay, z] = f.a, [bx, by] = f.b;
      const dx = bx - ax, dy = by - ay, L = Math.hypot(dx, dy) || 1, px = -dy / L, py = dx / L;
      for (let k = -2; k <= 2; k++) {
        const off = k * 0.11, bul = (0.22 + 0.16 * Math.abs(k)) * (k >= 0 ? 1 : -1);
        const pts: Proj[] = [];
        for (let s = 0; s <= 12; s++) {
          const v = s / 12, bend = f.kind === 'repel' ? bul * Math.sin(Math.PI * v) * 1.6 : bul * 0.7 * Math.sin(Math.PI * v);
          pts.push(this.project(ax + dx * v + px * (off + bend), ay + dy * v + py * (off + bend), z + 0.12 * Math.sin(Math.PI * v)));
        }
        c.beginPath(); pts.forEach((q, i) => (i ? c.lineTo(q.x, q.y) : c.moveTo(q.x, q.y)));
        let stroke: string | CanvasGradient;
        if (f.kind === 'attract') { const g = c.createLinearGradient(pts[0].x, pts[0].y, pts[12].x, pts[12].y); g.addColorStop(0, rgba(COL.neg, alpha * 0.9)); g.addColorStop(1, rgba(COL.pos, alpha * 0.9)); stroke = g; }
        else stroke = rgba(f.col, alpha * 0.85);
        c.strokeStyle = stroke; c.lineWidth = 6 * alpha * (this.W / 540); c.globalAlpha = 0.35; c.stroke(); c.globalAlpha = 1; c.lineWidth = 1.5 * (this.W / 540); c.stroke();
      }
    }
    c.globalCompositeOperation = 'source-over';
  }

  private drawRings(): void {
    const c = this.ctx;
    c.globalCompositeOperation = 'lighter';
    for (const r of this.rings) {
      const u = r.age / r.life, rad = (0.15 + 1.1 * E.outCubic(u)) * r.size, a = (1 - u) * 0.8;
      this.poly(this.circleWorld(r.x, r.y, 0.01, rad, 40)); c.strokeStyle = rgba(r.col, a); c.lineWidth = (5 * (1 - u) + 1) * (this.W / 540); c.stroke();
    }
    c.globalCompositeOperation = 'source-over';
  }

  private drawPiece(m: Model): void {
    const c = this.ctx;
    const g = this.pieceGeom(m), G = GEOMETRY[m.shape];
    const top = this.projAll(g.top), bot = this.projAll(g.bot);
    const cp = this.camPos();
    const cell = this.project(g.center[0], g.center[1], g.center[2]).s;
    const alpha = m.fall > 0 ? m.alpha * (1 - E.smooth(seg(m.fall, 0.72, 1))) : m.alpha;
    if (alpha <= 0.002) return;
    c.globalAlpha = alpha;
    // trail ghosts
    if (!this.reducedMotion && m.trail.length > 1) {
      for (let i = 0; i < m.trail.length; i++) {
        const tr = m.trail[i], a = (i + 1) / m.trail.length * 0.28;
        this.poly(this.projAll(g.top.map(q => [q[0] + tr[0] - m.x, q[1] + tr[1] - m.y, q[2]] as Vec3)));
        c.fillStyle = rgba(m.owner === WHITE ? [220, 224, 232] : [90, 100, 120], a * 0.5); c.fill();
      }
    }
    // side faces: the enamel poles, lit
    const [vcx, vcy] = this.V(0, 0); void vcx; void vcy;
    for (let i = 0; i < G.edges.length; i++) {
      const e = G.edges[i], n = g.nrm[i];
      const fc: Vec3 = [(g.top[e.a][0] + g.top[e.b][0] + g.bot[e.a][0] + g.bot[e.b][0]) / 4, (g.top[e.a][1] + g.top[e.b][1] + g.bot[e.a][1] + g.bot[e.b][1]) / 4, (g.top[e.a][2] + g.bot[e.a][2]) / 2];
      const [fx, fy] = this.V(fc[0], fc[1]);
      const nx = this.flipped ? -n[0] : n[0], ny = this.flipped ? -n[1] : n[1];
      const vis = nx * (cp[0] - fx) + ny * (cp[1] - fy) + n[2] * (cp[2] - fc[2]);
      if (vis <= 0) continue;
      const base = e.pole > 0 ? COL.pos : COL.neg, hot = e.pole > 0 ? COL.posHot : COL.negHot, inten = e.pole > 0 ? m.gPos : m.gNeg;
      const diff = 0.32 + 0.68 * Math.max(0, nx * LIGHT[0] + ny * LIGHT[1] + n[2] * LIGHT[2]);
      const col = mix(scaleC(base, diff), hot, clamp(inten - REST, 0, 1) * 0.8);
      this.poly([bot[e.a], bot[e.b], top[e.b], top[e.a]]); c.fillStyle = rgba(col, 1); c.fill();
      c.strokeStyle = 'rgba(0,0,0,0.35)'; c.lineWidth = 0.8; c.stroke();
    }
    // top face: the material
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const q of top) { x0 = Math.min(x0, q.x); y0 = Math.min(y0, q.y); x1 = Math.max(x1, q.x); y1 = Math.max(y1, q.y); }
    const gr = c.createLinearGradient(x0, y0, x1, y1);
    if (m.owner === WHITE) { gr.addColorStop(0, '#F2EEE4'); gr.addColorStop(0.55, '#C9C5BB'); gr.addColorStop(1, '#87837B'); }
    else { gr.addColorStop(0, '#3A3F4B'); gr.addColorStop(0.5, '#1A1D25'); gr.addColorStop(1, '#0A0B0F'); }
    this.poly(top); c.fillStyle = gr; c.fill();
    // specular
    c.save(); this.poly(top); c.clip();
    const sx = x0 * 0.65 + x1 * 0.35, sy = y0 * 0.7 + y1 * 0.3, sr = Math.max(3, (x1 - x0) * 0.7);
    const sg = c.createRadialGradient(sx, sy, 0, sx, sy, sr);
    sg.addColorStop(0, m.owner === WHITE ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.22)'); sg.addColorStop(1, 'rgba(255,255,255,0)');
    c.fillStyle = sg; c.fillRect(x0 - 2, y0 - 2, x1 - x0 + 4, y1 - y0 + 4); c.restore();
    // bevel ring: the pole colour along each top edge, inset
    const cx = top.reduce((s, q) => s + q.x, 0) / top.length, cy = top.reduce((s, q) => s + q.y, 0) / top.length;
    const inset = (q: Proj): Vec2 => [q.x + (cx - q.x) * 0.10, q.y + (cy - q.y) * 0.10];
    c.lineCap = 'butt'; c.lineJoin = 'round';
    for (const e of G.edges) {
      const A = inset(top[e.a]), B = inset(top[e.b]);
      const inten = e.pole > 0 ? m.gPos : m.gNeg, base = e.pole > 0 ? COL.pos : COL.neg, hot = e.pole > 0 ? COL.posHot : COL.negHot;
      c.beginPath(); c.moveTo(A[0], A[1]); c.lineTo(B[0], B[1]);
      c.strokeStyle = rgba(mix(base, hot, clamp(inten - REST, 0, 1) * 0.9), 1); c.lineWidth = Math.max(1.4, cell * 0.085); c.stroke();
      if (inten > 0.2) { c.globalCompositeOperation = 'lighter'; c.strokeStyle = rgba(hot, (inten - REST) * 0.55); c.lineWidth = Math.max(3, cell * 0.2); c.stroke(); c.globalCompositeOperation = 'source-over'; }
    }
    // rim
    this.poly(top); c.strokeStyle = m.owner === WHITE ? 'rgba(255,255,255,0.7)' : 'rgba(120,130,150,0.55)'; c.lineWidth = Math.max(0.8, cell * 0.02); c.stroke();
    c.globalAlpha = 1;
  }

  /** A steel bar laid across the join of two bonded pieces; violet flash when it snaps shut. */
  private drawBar(bar: Bar): void {
    const c = this.ctx;
    const a = this.models[bar.a], b = this.models[bar.b];
    if (!a.alive || !b.alive) return;
    const grow = bar.born > -1e8 ? E.smooth(seg(this.T, bar.born, bar.born + 0.14)) : 1;
    const flashA = bar.born > -1e8 ? 1 - E.smooth(seg(this.T, bar.born, bar.born + 0.55)) : 0;
    const dieA = bar.dying >= 0 ? 1 - E.smooth(seg(this.T, bar.dying, bar.dying + 0.18)) : 1;
    if (grow <= 0 || dieA <= 0) return;
    const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
    const dx = b.x - a.x, dy = b.y - a.y, L = Math.hypot(dx, dy) || 1;
    const ux = dx / L, uy = dy / L, px = -uy, py = ux;               // along the join, and across it
    const diagonal = Math.abs(dx) > 0.5 && Math.abs(dy) > 0.5;
    const half = (diagonal ? 0.30 : 0.42) * (0.5 + 0.5 * grow), thick = 0.06;
    const z0 = Math.max(a.z, b.z) + H + 0.005, z1 = z0 + 0.05 + 0.04 * (1 - grow);
    const corner = (s: number, t: number, z: number): Vec3 => [mx + px * s + ux * t, my + py * s + uy * t, z];
    const top = this.projAll([corner(-half, -thick, z1), corner(half, -thick, z1), corner(half, thick, z1), corner(-half, thick, z1)]);
    const front = this.projAll([corner(-half, -thick, z0), corner(half, -thick, z0), corner(half, -thick, z1), corner(-half, -thick, z1)]);
    c.globalAlpha = dieA;
    this.poly(front); c.fillStyle = '#6E7484'; c.fill();
    this.poly(top);
    const g = c.createLinearGradient(top[0].x, top[0].y, top[1].x, top[1].y); g.addColorStop(0, '#9AA1B0'); g.addColorStop(0.5, '#EEF1F7'); g.addColorStop(1, '#8B92A2');
    c.fillStyle = g; c.fill(); c.strokeStyle = 'rgba(0,0,0,0.5)'; c.lineWidth = 0.8; c.stroke();
    const glow = Math.max(flashA, bar.dying >= 0 ? 1 - dieA : 0);
    if (glow > 0.01) { c.globalCompositeOperation = 'lighter'; this.poly(top); c.fillStyle = rgba(COL.violet, glow * 0.9); c.fill(); c.globalCompositeOperation = 'source-over'; }
    c.globalAlpha = 1;
  }

  /** Consequence markers on the board surface. Never red or blue: those two colours mean polarity and nothing else. */
  private drawMarks(): void {
    const c = this.ctx;
    for (const p of this.previews) {
      const x = colOf(p.cell) + 0.5, y = rowOf(p.cell) + 0.5, z = 0.012;
      const s = this.project(x, y, z).s;
      const lw = Math.max(1.2, s * 0.05);
      const ink = 'rgba(231,233,240,';
      switch (p.kind) {
        case 'quiet':
          this.poly(this.circleWorld(x, y, z, 0.09, 20)); c.fillStyle = ink + '0.42)'; c.fill(); break;
        case 'push':
          this.poly(this.circleWorld(x, y, z, 0.2, 28)); c.strokeStyle = ink + '0.65)'; c.lineWidth = lw; c.stroke();
          this.chevron(x, y, z, ink + '0.65)', lw); break;
        case 'bond': case 'grab':
          this.bar(x, y, z, rgba(COL.violet, 1));
          if (p.kind === 'grab') { this.poly(this.circleWorld(x, y, z, 0.25, 28)); c.strokeStyle = rgba(COL.violet, 0.9); c.lineWidth = lw; c.stroke(); }
          break;
        case 'capture':
          this.poly(this.circleWorld(x, y, z, 0.24, 28)); c.strokeStyle = ink + '0.95)'; c.lineWidth = lw; c.stroke();
          this.poly(this.circleWorld(x, y, z, 0.1, 20)); c.fillStyle = ink + '0.95)'; c.fill(); break;
        case 'danger':
          this.poly(this.circleWorld(x, y, z, 0.22, 28)); c.strokeStyle = rgba(COL.amber, 1); c.lineWidth = lw; c.stroke();
          { const a = this.project(x - 0.13, y - 0.13, z), b = this.project(x + 0.13, y + 0.13, z); c.beginPath(); c.moveTo(a.x, a.y); c.lineTo(b.x, b.y); c.lineCap = 'round'; c.stroke(); }
          break;
        default: break;
      }
      if (p.alsoLoses) { this.poly(this.circleWorld(x + 0.24, y + 0.24, z, 0.06, 12)); c.fillStyle = rgba(COL.amber, 1); c.fill(); }
    }
  }
  private chevron(x: number, y: number, z: number, stroke: string, lw: number): void {
    const c = this.ctx, fwd = this.flipped ? -1 : 1;
    const a = this.project(x - 0.09, y - 0.05 * fwd, z), b = this.project(x, y + 0.07 * fwd, z), d = this.project(x + 0.09, y - 0.05 * fwd, z);
    c.beginPath(); c.moveTo(a.x, a.y); c.lineTo(b.x, b.y); c.lineTo(d.x, d.y); c.strokeStyle = stroke; c.lineWidth = lw; c.lineCap = 'round'; c.lineJoin = 'round'; c.stroke();
  }
  private bar(x: number, y: number, z: number, fill: string): void {
    this.poly(this.projAll([[x - 0.2, y - 0.05, z], [x + 0.2, y - 0.05, z], [x + 0.2, y + 0.05, z], [x - 0.2, y + 0.05, z]]));
    this.ctx.fillStyle = fill; this.ctx.fill();
  }

  private drawParticles(): void {
    if (!this.particles.length) return;
    const c = this.ctx;
    c.globalCompositeOperation = 'lighter';
    for (const q of this.particles) {
      const u = q.age / q.life, a = 1 - u, p = this.project(q.x, q.y, Math.max(-0.3, q.z));
      const r = q.r * a * (p.s / 200 + 0.4) * (this.W / 540);
      const gr = c.createRadialGradient(p.x, p.y, 0, p.x, p.y, r * 2.2);
      gr.addColorStop(0, rgba(q.col, a)); gr.addColorStop(0.35, rgba(q.col, a * 0.6)); gr.addColorStop(1, rgba(q.col, 0));
      c.fillStyle = gr; c.beginPath(); c.arc(p.x, p.y, r * 2.2, 0, Math.PI * 2); c.fill();
    }
    c.globalCompositeOperation = 'source-over';
  }

  private drawPost(): void {
    const c = this.ctx, W = this.W, Hh = this.Hpx;
    const vg = c.createRadialGradient(W / 2, Hh / 2, Math.min(W, Hh) * 0.35, W / 2, Hh / 2, Math.max(W, Hh) * 0.8);
    vg.addColorStop(0, 'rgba(0,0,0,0)'); vg.addColorStop(1, 'rgba(0,0,0,0.65)'); c.fillStyle = vg; c.fillRect(0, 0, W, Hh);
    if (this.flash > 0.005) { c.fillStyle = `rgba(255,255,255,${this.flash * 0.5})`; c.fillRect(0, 0, W, Hh); }
  }
}

function pointInPoly(x: number, y: number, pts: Proj[]): boolean {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const xi = pts[i].x, yi = pts[i].y, xj = pts[j].x, yj = pts[j].y;
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** A small stage showing one piece, for legends. */
export function renderPieceIcon(canvas: HTMLCanvasElement, shape: ShapeId, side: Side): Stage {
  const st = new Stage(canvas, { onCell: () => {} });
  st.showIcon(shape, side);
  return st;
}
